import { ordersApi } from './api';
import {
  getOrderProductLabel,
  getOrderStatusLabel,
  type OrderStatusName,
} from './orderTypes';

export interface OrderRecord {
  id: string;
  courseId?: string | null;
  courseName: string | null;
  unitId?: number | null;
  unitName?: string | null;
  type?: string | number | null;
  typeId?: number | null;
  promoCode: string | null;
  discountAmount: number;
  netPrice: number;
  orderDate: string;
  status: OrderStatusName;
}

export interface OrderFilters {
  orderId?: string;
  status?: string;
  type?: string;
  studentName?: string;
  studentPhoneNumber?: string;
  promoCode?: string;
  startDate?: string;
  endDate?: string;
  teacherId?: string;
}

export interface StatusSlice {
  name: string;
  value: number;
  status: OrderStatusName;
}

export interface CourseRevenueRow {
  name: string;
  revenue: number;
  orders: number;
}

export interface TimelineRow {
  date: string;
  label: string;
  orders: number;
  revenue: number;
}

export interface PromoCodeRow {
  code: string;
  uses: number;
  discount: number;
}

export interface OrderAnalytics {
  totalOrders: number;
  completedCount: number;
  pendingCount: number;
  cancelledCount: number;
  refundedCount: number;
  totalRevenue: number;
  todayRevenue: number;
  monthRevenue: number;
  todayCompletedOrders: number;
  monthCompletedOrders: number;
  totalDiscounts: number;
  averageOrderValue: number;
  statusDistribution: StatusSlice[];
  revenueByCourse: CourseRevenueRow[];
  ordersOverTime: TimelineRow[];
  topPromoCodes: PromoCodeRow[];
}

export function buildOrderFilters(input: {
  orderIdFilter: string;
  statusFilter: string;
  typeFilter?: string;
  studentNameFilter: string;
  studentPhoneFilter: string;
  promoCodeFilter: string;
  startDateFilter: string;
  endDateFilter: string;
  teacherIdFilter?: string;
}): OrderFilters | undefined {
  const filters: OrderFilters = {};

  if (input.orderIdFilter.trim()) filters.orderId = input.orderIdFilter.trim();
  if (input.statusFilter) filters.status = input.statusFilter;
  if (input.typeFilter) filters.type = input.typeFilter;
  if (input.studentNameFilter) filters.studentName = input.studentNameFilter;
  if (input.studentPhoneFilter) filters.studentPhoneNumber = input.studentPhoneFilter;
  if (input.promoCodeFilter.trim()) filters.promoCode = input.promoCodeFilter.trim();
  if (input.startDateFilter) filters.startDate = input.startDateFilter;
  if (input.endDateFilter) filters.endDate = input.endDateFilter;
  if (input.teacherIdFilter) filters.teacherId = input.teacherIdFilter;

  return Object.keys(filters).length > 0 ? filters : undefined;
}

export async function fetchAllOrdersForAnalytics(filters?: OrderFilters): Promise<OrderRecord[]> {
  const pageSize = 500;
  let pageNumber = 1;
  let totalPages = 1;
  const allOrders: OrderRecord[] = [];

  do {
    const response = await ordersApi.getAllOrders(pageNumber, pageSize, filters);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch orders for analytics');
    }

    const pageData = response.data?.data || [];
    allOrders.push(...pageData);
    totalPages = response.data?.totalPages || 1;
    pageNumber += 1;
  } while (pageNumber <= totalPages);

  return allOrders;
}

function formatDayLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' });
}

function getLocalDateKey(dateString: string): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getLocalMonthKey(dateString: string): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function getCurrentDateKey(): string {
  return getLocalDateKey(new Date().toISOString());
}

function getCurrentMonthKey(): string {
  return getLocalMonthKey(new Date().toISOString());
}

export function getCurrentMonthLabel(): string {
  return new Date().toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' });
}

export function computeOrderAnalytics(orders: OrderRecord[]): OrderAnalytics {
  const completedOrders = orders.filter((order) => order.status === 'Completed');
  const totalRevenue = completedOrders.reduce((sum, order) => sum + order.netPrice, 0);
  const totalDiscounts = orders.reduce((sum, order) => sum + order.discountAmount, 0);
  const todayKey = getCurrentDateKey();
  const monthKey = getCurrentMonthKey();

  let todayRevenue = 0;
  let monthRevenue = 0;
  let todayCompletedOrders = 0;
  let monthCompletedOrders = 0;

  for (const order of completedOrders) {
    const orderDay = getLocalDateKey(order.orderDate);
    const orderMonth = getLocalMonthKey(order.orderDate);

    if (orderDay === todayKey) {
      todayRevenue += order.netPrice;
      todayCompletedOrders += 1;
    }

    if (orderMonth === monthKey) {
      monthRevenue += order.netPrice;
      monthCompletedOrders += 1;
    }
  }

  const statusCounts: Record<OrderStatusName, number> = {
    Completed: 0,
    Pending: 0,
    Cancelled: 0,
    Refunded: 0,
  };

  for (const order of orders) {
    if (order.status in statusCounts) {
      statusCounts[order.status] += 1;
    }
  }

  const statusDistribution: StatusSlice[] = (Object.keys(statusCounts) as OrderStatusName[])
    .map((status) => ({
      status,
      name: getOrderStatusLabel(status),
      value: statusCounts[status],
    }))
    .filter((item) => item.value > 0);

  const courseMap = new Map<string, { revenue: number; orders: number }>();
  for (const order of completedOrders) {
    const name = getOrderProductLabel(order);
    const current = courseMap.get(name) || { revenue: 0, orders: 0 };
    courseMap.set(name, {
      revenue: current.revenue + order.netPrice,
      orders: current.orders + 1,
    });
  }

  const revenueByCourse = Array.from(courseMap.entries())
    .map(([name, stats]) => ({ name, ...stats }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 8);

  const timelineMap = new Map<string, { orders: number; revenue: number }>();
  for (const order of orders) {
    const dateKey = order.orderDate.slice(0, 10);
    const current = timelineMap.get(dateKey) || { orders: 0, revenue: 0 };
    timelineMap.set(dateKey, {
      orders: current.orders + 1,
      revenue: current.revenue + (order.status === 'Completed' ? order.netPrice : 0),
    });
  }

  const ordersOverTime = Array.from(timelineMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, stats]) => ({
      date,
      label: formatDayLabel(date),
      orders: stats.orders,
      revenue: stats.revenue,
    }));

  const promoMap = new Map<string, { uses: number; discount: number }>();
  for (const order of orders) {
    const code = order.promoCode?.trim();
    if (!code) continue;
    const current = promoMap.get(code) || { uses: 0, discount: 0 };
    promoMap.set(code, {
      uses: current.uses + 1,
      discount: current.discount + order.discountAmount,
    });
  }

  const topPromoCodes = Array.from(promoMap.entries())
    .map(([code, stats]) => ({ code, ...stats }))
    .sort((a, b) => b.uses - a.uses)
    .slice(0, 6);

  return {
    totalOrders: orders.length,
    completedCount: statusCounts.Completed,
    pendingCount: statusCounts.Pending,
    cancelledCount: statusCounts.Cancelled,
    refundedCount: statusCounts.Refunded,
    totalRevenue,
    todayRevenue,
    monthRevenue,
    todayCompletedOrders,
    monthCompletedOrders,
    totalDiscounts,
    averageOrderValue: completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0,
    statusDistribution,
    revenueByCourse,
    ordersOverTime,
    topPromoCodes,
  };
}

export function formatOrderCurrency(amount: number): string {
  return new Intl.NumberFormat('ar-EG', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function truncateChartLabel(value: string, max = 18): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, Math.max(0, max - 1))}…`;
}
